import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { CheckboxWithLabel } from 'formik-mui';
import { useTheme } from '@mui/material/styles';
import * as Yup from 'yup';
import { Seo } from 'src/components/seo';
import { queryAggregatorDisclosures } from 'src/api/query-logic/query-aggregator-disclosures';
import { useAggregatorAcceptDisclosures } from 'src/api/query-logic/query-aggregator-mutations';
import DisclosureHeading from 'src/e2e-redesign/components/DisclosureHaeding';
import LoadingState from 'src/e2e-redesign/components/LoadingState';
import SingleLogo from 'src/e2e-redesign/components/SingleLogo';
import { AggregatorDisclosure } from 'src/interfaces/aggregator.interfaces';
import AggregatorError from './aggregator-error';
import { getAggregatorErrorMessage } from 'src/utils/aggregator-error-helpers';
import { AGGREGATOR_ERROR_MESSAGES, AGGREGATOR_POST_MESSAGE_TYPES } from 'src/utils/aggregator.constants';

/**
 * Generates a UUID v4 compatible session ID with sess_ prefix
 * Matches backend expected format: sess_{uuid}
 * Uses crypto.randomUUID() if available, otherwise falls back to a simple UUID generator
 * 
 * Note: Ideally, session IDs should be generated by the aggregator or backend.
 * This is only used as a fallback when no session_id is provided.
 */
const generateSessionId = (): string => {
  let uuid: string;
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    uuid = crypto.randomUUID();
  } else {
    // Fallback UUID v4 generator
    uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  // Prefix with sess_ to match backend expected format
  return `sess_${uuid}`;
};

export default function AggregatorDisclosures() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const lenderId = searchParams.get('lender_id');
  const sessionIdFromUrl = searchParams.get('session_id');
  const theme = useTheme();
  const [accepted, setAccepted] = useState(false);

  // Get session_id from URL param, or generate one if not provided
  // The aggregator parent page should pass session_id via URL parameter
  // Session ID is created by aggregator when user starts application and is used
  // to link form submission (which happens first) to disclosure acceptance (which triggers E2E)
  // If session_id is provided from URL, use it as-is (backend will normalize if needed)
  // If not provided, generate one with sess_ prefix to match backend format
  const [sessionId] = useState<string>(() => {
    if (sessionIdFromUrl) {
      // Use session_id from URL as-is - backend should normalize/validate it
      return sessionIdFromUrl;
    }
    // Fallback: generate session_id with sess_ prefix to match backend expected format
    return generateSessionId();
  });
  const acceptDisclosuresMutation = useAggregatorAcceptDisclosures();

  // Listen for messages from parent window
  // Handles both iframe URL updates and session_id from parent
  // IMPORTANT: This useEffect must be called unconditionally (before any early returns)
  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle iframe URL updates
      if (event.data?.type === AGGREGATOR_POST_MESSAGE_TYPES.UPDATE_IFRAME && event.data?.iframe_url) {
        // In production, validate event.origin for security:
        // if (event.origin !== 'https://trusted-third-party-domain.com') return;

        // Validate that iframe_url is a valid URL
        try {
          const url = new URL(event.data.iframe_url);
          // Only allow navigation to our own domain (security)
          const allowedOrigin = window.location.origin;
          if (url.origin === allowedOrigin || process.env.REACT_APP_ENVIRONMENT === 'local') {
            window.location.href = event.data.iframe_url;
          }
        } catch (e) {
          // Invalid iframe_url provided - silently ignore
        }
      }
      // Note: session_id from postMessage is not handled here because
      // we prefer URL parameter for initial load. If parent sends session_id
      // via postMessage, it should be included in the iframe URL instead.
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const { data: disclosures, isLoading, isError, error } = queryAggregatorDisclosures({
    reqData: {
      query: token ? { token, ...(lenderId ? { lender_id: lenderId } : {}) } : undefined,
    },
    queryKey: [token || '', lenderId || ''],
    queryOpt: {
      enabled: !!token,
    },
  });

  if (!token) {
    return <AggregatorError message={AGGREGATOR_ERROR_MESSAGES.MISSING_TOKEN} errorCode={400} />;
  }

  if (isLoading) {
    return <LoadingState title="Loading Disclosures" paragraph="Please wait while we load the disclosure information." />;
  }

  if (isError) {
    const errorMessage = getAggregatorErrorMessage(error, 'disclosures');
    return <AggregatorError message={errorMessage} errorCode={(error as any)?.statusCode} />;
  }

  if (!disclosures || disclosures.length === 0) {
    return <AggregatorError message={AGGREGATOR_ERROR_MESSAGES.NO_DISCLOSURES} errorCode={404} />;
  }

  const sortedDisclosures = [...disclosures].sort((a, b) => a.sequence - b.sequence);

  // If disclosures are already accepted, show confirmation
  if (accepted) {
    return (
      <>
        <Seo title="Disclosures Accepted" />
        <Box sx={{ textAlign: 'center', padding: '40px' }}>
          <Typography variant="h4" sx={{ marginBottom: '20px', fontWeight: 600, color: theme.palette.primary.main }}>
            Disclosures Accepted
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '20px' }}>
            Thank you for accepting the disclosures. Your application has been submitted and is being processed.
          </Typography>
        </Box>
      </>
    );
  }

  // Build form validation schema
  const initialValues: Record<string, boolean> = {};
  const validationFields: Record<string, any> = {};

  sortedDisclosures.forEach((disclosure) => {
    const fieldName = `disclosure_${disclosure._id}`;
    const shouldBypass = disclosure.disclosure_type === 'fp' || disclosure.name?.toLowerCase().includes('bypass');
    initialValues[fieldName] = shouldBypass ? true : false;
    if (!shouldBypass) {
      validationFields[fieldName] = Yup.boolean().oneOf([true], 'You must agree to the terms.');
    }
  });

  const validationSchema = Yup.object().shape(validationFields);

  const handleSubmit = async () => {
    if (!token) {
      return;
    }

    try {
      // Call acceptance endpoint with session_id
      // Backend will link this disclosure acceptance to the existing AASO (created during form submission)
      // and trigger E2E submission to lenders
      const showOffers = searchParams.get('showOffers');
      const acceptanceData = await acceptDisclosuresMutation.mutateAsync({
        token,
        session_id: sessionId,
        showOffers: showOffers ?? undefined,
      });

      // Send session_id and iframe_url to parent window via postMessage
      // In the new flow: form submission happens first, then disclosures
      // When disclosures are accepted, backend links to existing AASO and triggers E2E submission
      // Backend returns iframe_url for offers - parent can use this to update iframe
      if (window.parent && window.parent !== window) {
        const payload = {
          type: AGGREGATOR_POST_MESSAGE_TYPES.DISCLOSURES_ACCEPTED,
          session_id: acceptanceData.session_id,
          lender_id: acceptanceData.lender_id,
          expires_at: acceptanceData.expires_at,
          iframe_url: showOffers !== 'false' ? acceptanceData.iframe_url : undefined, // Include offers iframe URL
          submission_id: acceptanceData.submission_id, // Include submission ID for tracking
          acceptance_id: acceptanceData.acceptance_id, // Include acceptance ID if present
        };

        window.parent.postMessage(
          payload,
          '*' // In production, specify exact origin for security
        );
      }

      // Navigate to offers iframe URL if available
      // This allows the iframe to automatically show offers after disclosure acceptance
      // Only redirect if showOffers query param is not explicitly set to 'false'
      const shouldRedirect = showOffers !== 'false';
      if (acceptanceData.iframe_url && shouldRedirect) {
        window.location.href = acceptanceData.iframe_url;
      } else {
        // Fallback: show success message if no iframe_url (shouldn't happen in normal flow)
        setAccepted(true);
      }
    } catch (error) {
      // Error is handled by the mutation
      throw error;
    }
  };

  return (
    <>
      <Seo title="Disclosures" />
      <Box>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ isSubmitting, values }) => {
            const allAccepted = sortedDisclosures.every((disclosure) => {
              const fieldName = `disclosure_${disclosure._id}`;
              const shouldBypass = disclosure.disclosure_type === 'fp' || disclosure.name?.toLowerCase().includes('bypass');
              return shouldBypass || values[fieldName] === true;
            });

            return (
              <Form>
                {sortedDisclosures.map((disclosure: AggregatorDisclosure) => {
                  const fieldName = `disclosure_${disclosure._id}`;
                  const shouldBypass = disclosure.disclosure_type === 'fp' || disclosure.name?.toLowerCase().includes('bypass');

                  return (
                    <Box key={disclosure._id} sx={{ marginBottom: '40px' }}>
                      {disclosure.lender_logo && (
                        <Box mb={2}>
                          <SingleLogo
                            image={{
                              src: disclosure.lender_logo,
                              alt: disclosure.lender_name || 'Lender Logo',
                            }}
                          />
                        </Box>
                      )}
                      <DisclosureHeading>Disclosures</DisclosureHeading>
                      <Box
                        sx={{
                          border: '1px solid #E0E8F5',
                          borderRadius: '15px',
                          padding: '10px',
                          maxHeight: { xs: '450px', md: '300px' },
                          overflowY: 'scroll',
                          marginBottom: '20px',
                          '&::-webkit-scrollbar': {
                            width: '12px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: '#ffffff',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: '#E0E8F5',
                            borderRadius: '15px',
                          },
                          '&::-webkit-scrollbar-thumb:hover': {
                            background: '#B0C4DE',
                          },
                        }}
                      >
                        {disclosure.dynamic_link ? (
                          <iframe
                            src={disclosure.dynamic_link}
                            title={disclosure.name}
                            style={{ width: '100%', height: '100%', border: 'none', minHeight: '300px' }}
                          />
                        ) : (
                          <div
                            dangerouslySetInnerHTML={{
                              __html: disclosure.document || '',
                            }}
                          />
                        )}
                      </Box>
                      {!shouldBypass && (
                        <Box mb={2}>
                          <Field
                            component={CheckboxWithLabel}
                            name={fieldName}
                            type="checkbox"
                            Label={{
                              label: 'I agree to the terms above.'
                            }}
                          />
                          <ErrorMessage
                            name={fieldName}
                            component="div"
                            render={(msg) => <div style={{ color: theme.palette.error.main }}>{msg}</div>}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
                <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '30px', marginBottom: '30px' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!allAccepted || isSubmitting || acceptDisclosuresMutation.isPending}
                    sx={{ minWidth: '200px' }}
                  >
                    {isSubmitting || acceptDisclosuresMutation.isPending ? 'Accepting...' : 'I Agree'}
                  </Button>
                  {acceptDisclosuresMutation.isError && (
                    <Typography variant="body2" color="error" sx={{ marginTop: '10px', textAlign: 'center' }}>
                      Failed to accept disclosures. Please try again.
                    </Typography>
                  )}
                </Box>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </>
  );
}
